import React, { useMemo } from 'react';
import { FixedSizeList as List } from 'react-window';
import {
  ListItemButton,
  ListItemText,
  Radio,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import { Icon } from '@mui/material';
import MDTypography from 'src/components/md/MDTypography';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';

const ITEM_HEIGHT = 75;

interface VariantItemData {
  variants: Array<{
    id: string;
    originMetadata?: unknown;
    sku?: string | null;
  }>;
  selectedVariantId?: string;
  onSelect: (variantId: string) => void;
}

const VariantListItem = ({
  index,
  style,
  data,
}: {
  index: number;
  style: React.CSSProperties;
  data: VariantItemData;
}) => {
  const { variants, selectedVariantId, onSelect } = data;
  const variant = variants[index];

  const { data: variantData, isLoading, error } = useProductVariant(variant.id);
  const productVariant = variantData?.productVariant;
  const attributeDescriptions = variantData?.attributeDescriptions || [];
  const hasAttributes = attributeDescriptions.length > 0;

  return (
    <div style={style}>
      <ListItemButton
        dense
        onClick={() => onSelect(variant.id)}
        sx={{
          height: ITEM_HEIGHT,
          minHeight: ITEM_HEIGHT,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor:
            selectedVariantId === variant.id
              ? 'rgba(0, 164, 164, 0.12)'
              : 'transparent',
          '&:hover': {
            backgroundColor:
              selectedVariantId === variant.id
                ? 'rgba(0, 164, 164, 0.12)'
                : 'rgba(0, 0, 0, 0.04)',
          },
          alignItems: 'flex-start',
          py: 1,
        }}
      >
        <Radio
          checked={selectedVariantId === variant.id}
          size="small"
          sx={{
            p: 0.5,
            ml: 2,
            mr: 0.5,
            mt: 0.5,
            alignSelf: 'flex-start',
            color: 'rgba(0, 0, 0, 0.54)',
            '&.Mui-checked': {
              color: '#00a4a4',
            },
          }}
        />
        {isLoading ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
            ml={2}
          >
            <CircularProgress size={20} />
            <MDTypography variant="body2" ml={2}>
              Loading...
            </MDTypography>
          </Box>
        ) : error ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            flex={1}
            ml={2}
          >
            <Icon sx={{ color: 'error.main', mr: 1 }}>error_outline</Icon>
            <MDTypography variant="body2" color="error.main">
              Error loading variant
            </MDTypography>
          </Box>
        ) : productVariant ? (
          <ListItemText
            primary={
              <MDTypography
                variant="body2"
                fontWeight={
                  selectedVariantId === variant.id ? 'medium' : 'regular'
                }
                color={
                  selectedVariantId === variant.id ? '#00a4a4' : 'text.primary'
                }
              >
                {productVariant.originMetadata?.NAMEINVC ||
                  productVariant.sku ||
                  'Unknown'}
                {productVariant.originMetadata?.PRODUCT_ID &&
                  ` (${String(productVariant.originMetadata.PRODUCT_ID)})`}
              </MDTypography>
            }
            secondary={
              hasAttributes ? (
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={0.5}
                  mt={0.5}
                  sx={{ maxWidth: '100%' }}
                >
                  {attributeDescriptions.map((attr, index) => (
                    <Chip
                      key={index}
                      label={
                        <Box component="span">
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 500,
                              opacity: 0.9,
                            }}
                          >
                            {attr.name}:
                          </Box>{' '}
                          <Box component="span" sx={{ opacity: 0.8 }}>
                            {attr.value}
                          </Box>
                        </Box>
                      }
                      size="small"
                      variant="outlined"
                      sx={{
                        height: '20px',
                        fontSize: '0.65rem',
                        borderColor:
                          selectedVariantId === variant.id
                            ? 'rgba(0, 164, 164, 0.5)'
                            : 'rgba(0, 0, 0, 0.23)',
                        color:
                          selectedVariantId === variant.id
                            ? '#007f7f'
                            : 'text.secondary',
                        backgroundColor:
                          selectedVariantId === variant.id
                            ? 'rgba(0, 164, 164, 0.08)'
                            : 'transparent',
                        '& .MuiChip-label': {
                          px: 0.75,
                          py: 0,
                        },
                      }}
                    />
                  ))}
                </Box>
              ) : undefined
            }
          />
        ) : (
          <ListItemText primary="Variant not found" />
        )}
      </ListItemButton>
    </div>
  );
};

interface Props {
  variants: Array<{
    id: string;
    originMetadata?: unknown;
    sku?: string | null;
  }>;
  selectedVariantId?: string;
  onSelect: (variantId: string) => void;
  maxHeight?: number;
}

const VirtualizedVariantList: React.FC<Props> = ({
  variants,
  selectedVariantId,
  onSelect,
  maxHeight = 400,
}) => {
  const itemData = useMemo(
    () => ({
      variants,
      selectedVariantId,
      onSelect,
    }),
    [variants, selectedVariantId, onSelect],
  );

  if (variants.length === 0) {
    return (
      <Box p={2} textAlign="center">
        <MDTypography variant="body2" color="text.secondary">
          No variants found
        </MDTypography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <List
        height={Math.min(maxHeight, variants.length * ITEM_HEIGHT)}
        width="100%"
        itemCount={variants.length}
        itemSize={ITEM_HEIGHT}
        itemData={itemData}
        overscanCount={5} // Render a few extra items for smooth scrolling
      >
        {VariantListItem}
      </List>
    </Box>
  );
};

export default VirtualizedVariantList;
